<template>
    <Form as="" :validation-schema="schema" v-slot="{ meta, handleSubmit }">
        <bs5-base-modal :title="$t('licenses.generateTitle')" :visible="visible" @close="closeModal">
            <template v-slot:default>
                <form novalidate @submit.prevent="handleSubmit($event, generateLicense)">
                    <div class="mb-3">
                        <bs5-base-label for="licenceRequest">{{ $t("licenses.requestFile") }}</bs5-base-label>
                        <Field name="licenceRequest" :model-value="licenceRequest"
                            v-slot="{ handleChange, handleBlur, meta: fileMeta, errorMessage }">
                            <input id="licenceRequest" type="file" accept=".xml,application/xml,text/xml"
                                class="form-control"
                                :class="{ 'is-valid': fileMeta.valid && fileMeta.touched, 'is-invalid': !!errorMessage && fileMeta.touched }"
                                @change="onFileChange($event, handleChange)" @blur="handleBlur" />
                            <div v-if="errorMessage && fileMeta.touched" class="invalid-feedback d-block">
                                {{ errorMessage }}
                            </div>
                        </Field>
                        <div class="form-text">{{ $t("licenses.requestFileHint") }}</div>
                    </div>

                    <bs5-form-field id="validTo" name="validTo" type="date" :label="$t('licenses.validTo')"
                        :hint="$t('licenses.validToHint')" v-model="license.validTo" />

                    <div class="mb-3">
                        <bs5-base-label>{{ $t("licenses.modules") }}</bs5-base-label>
                        <div>
                            <label v-for="(label, key) in availableFeatures" :key="key" class="form-check">
                                <input type="checkbox" class="form-check-input" v-model="license[key]" />
                                {{ $t(`licenses.features.${key}`) }}
                            </label>
                        </div>
                    </div>
                    <button type="submit" class="d-none" tabindex="-1" aria-hidden="true"></button>
                </form>
            </template>

            <template v-slot:footer>
                <bs5-cancel-button @click="closeModal" />
                <bs5-save-button :loading="saving" :disabled="!meta.valid" @click="handleSubmit($event, generateLicense)">
                    <i class="bi bi-file-earmark-plus"></i>{{ $t("licenses.generate") }}
                </bs5-save-button>
            </template>
        </bs5-base-modal>
    </Form>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { Field, Form } from "vee-validate";
import * as yup from "yup";
import { t } from "@/helpers/i18n";
import licenseService from "@/services/license.service";
import { LicenseRequest } from "@/models/licence-request.model";
import { showToast } from "@/utils/toast";

// Dzisiejsza data w formacie pola typu "date" (RRRR-MM-DD, czas lokalny)
function todayIso(): string {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export default defineComponent({
    name: "GenerateLicenseModal",
    components: { Field, Form },
    props: {
        visible: { type: Boolean, required: true },
        clientId: { type: String, required: true },
    },
    emits: ["close", "license-generated"],
    data() {
        return {
            license: ref<Omit<LicenseRequest, "licenceRequest">>({
                validTo: "",
                hasDiscussion: false,
                hasAV: false,
                hasFA: false,
                hasVoting: false,
                hasNfc: false,
                hasOnlineStatus: false,
                hasWirelessData: false,
                hasScreenIdentification: false,
            }),
            licenceRequest: ref<File | null>(null),
            saving: ref(false),
            availableFeatures: {
                hasDiscussion: "Discussion",
                hasAV: "Ad vocem",
                hasFA: "Formal application",
                hasVoting: "Voting",
                hasNfc: "NFC",
                hasOnlineStatus: "Online Status",
                hasWirelessData: "Wireless Data",
                hasScreenIdentification: "Screen Identification",
            },
        };
    },
    computed: {
        schema() {
            // Walidacja tylko po stronie formularza - wysyłane dane licencji są takie same jak wcześniej
            return yup.object({
                licenceRequest: yup.mixed()
                    .test("required", t("validation.fileRequired"), value => value instanceof File)
                    .test("xml", t("validation.fileXml"), value => !(value instanceof File) || /\.xml$/i.test(value.name)),
                validTo: yup.string()
                    .test("notPast", t("validation.dateNotPast"), value => !value || value >= todayIso()),
            });
        },
    },
    methods: {
        onFileChange(event: Event, handleChange: (value: unknown) => void) {
            this.handleFileUpload(event);
            handleChange(this.licenceRequest);
        },
        handleFileUpload(event: Event) {
            const target = event.target as HTMLInputElement;
            if (target.files?.length) {
                this.licenceRequest = target.files[0];
            }
        },
        async generateLicense() {
            this.saving = true;
            try {
                await licenseService.generateLicense(this.clientId, {
                    ...this.license,
                    licenceRequest: this.licenceRequest,
                });
                this.$emit("license-generated");
                this.closeModal();
                showToast(t("licenses.generated"), "success");
            } catch (error) {
                showToast(t("licenses.generateFailed"), "error");
                console.log(error);
            } finally {
                this.saving = false;
            }
        },
        closeModal() {
            this.$emit("close");
        },
    },
});
</script>